import api from '@/lib/api';
import type {
  CandidateDocumentResponse,
  CVUploadAndExtractResponse,
  CVUploadResponse,
  ApplyCVDataOptions,
  ApplyCVDataResponse,
  ExtractedCVData,
} from '../types';

const BASE_URL = '/itc/candidates';

/**
 * Document service for CV upload and extraction
 */
export const documentsService = {
  /**
   * Get all documents for a candidate
   */
  async list(candidateId: number): Promise<CandidateDocumentResponse[]> {
    const { data } = await api.get(`${BASE_URL}/${candidateId}/documents`);
    return data;
  },

  /**
   * Get active CV for a candidate
   */
  async getActiveCV(candidateId: number): Promise<CandidateDocumentResponse | null> {
    try {
      const { data } = await api.get(`${BASE_URL}/${candidateId}/documents/cv`);
      return data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get document by ID with download URL
   */
  async getById(
    candidateId: number,
    documentId: number
  ): Promise<CandidateDocumentResponse> {
    const { data } = await api.get(
      `${BASE_URL}/${candidateId}/documents/${documentId}`
    );
    return data;
  },

  /**
   * Upload a CV file only (without extraction)
   */
  async uploadCV(candidateId: number, file: File): Promise<CVUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post(
      `${BASE_URL}/${candidateId}/documents/cv`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  },

  /**
   * Upload CV and extract data using AI
   * This is the main method for CV processing
   */
  async uploadAndExtract(
    candidateId: number,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<CVUploadAndExtractResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post(
      `${BASE_URL}/${candidateId}/documents/cv/extract`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(progress);
          }
        },
      }
    );
    return data;
  },

  /**
   * Re-extract data from an existing CV document
   */
  async extractFromDocument(
    candidateId: number,
    documentId: number
  ): Promise<{ extraction: { success: boolean; data: ExtractedCVData | null; error?: string } }> {
    const { data } = await api.post(
      `${BASE_URL}/${candidateId}/documents/${documentId}/extract`
    );
    return data;
  },

  /**
   * Apply extracted CV data to candidate profile
   */
  async applyExtractedData(
    candidateId: number,
    extractedData: ExtractedCVData,
    options: ApplyCVDataOptions = {}
  ): Promise<ApplyCVDataResponse> {
    const { data } = await api.post(
      `${BASE_URL}/${candidateId}/apply-cv-data`,
      {
        data: extractedData,
        options,
      }
    );
    return data;
  },

  /**
   * Delete a document
   */
  async delete(candidateId: number, documentId: number): Promise<void> {
    await api.delete(`${BASE_URL}/${candidateId}/documents/${documentId}`);
  },

  /**
   * Get download URL for a document
   */
  async getDownloadUrl(
    candidateId: number,
    documentId: number
  ): Promise<string> {
    const doc = await this.getById(candidateId, documentId);
    if (!doc.downloadUrl) {
      throw new Error('Download URL not available');
    }
    return doc.downloadUrl;
  },

  /**
   * Download a document file as blob (authenticated)
   * @param candidateId - Candidate ID
   * @param documentId - Document ID
   * @returns Blob of the file
   */
  async downloadBlob(candidateId: number, documentId: number): Promise<Blob> {
    const { data } = await api.get(
      `${BASE_URL}/${candidateId}/documents/${documentId}/download`,
      { responseType: 'blob' }
    );
    return data;
  },

  /**
   * Trigger browser download of a blob
   * @param blob - File blob
   * @param filename - Name for the downloaded file
   */
  triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Get a temporary object URL for document preview (authenticated)
   * The caller must revoke the URL when done via window.URL.revokeObjectURL()
   * @param candidateId - Candidate ID
   * @param documentId - Document ID
   * @returns Temporary object URL string
   */
  async getPreviewUrl(candidateId: number, documentId: number): Promise<string> {
    const blob = await this.downloadBlob(candidateId, documentId);
    return window.URL.createObjectURL(blob);
  },
};
